<template>
  <div class="camera-capture fixed inset-0 z-30 bg-black text-white">
    <!-- Rotate-to-landscape overlay -->
    <div v-if="showRotateOverlay" class="rotate-overlay z-40">
      <button class="close-button absolute top-4 left-4" aria-label="Close" @click="$emit('close')">
        <Icon type="xmark" fa-style="far" class="text-lg" />
      </button>
      <Icon type="mobile-screen" fa-style="far" class="rotate-icon text-6xl" />
      <div class="text-xl font-semibold mt-8">Rotate your phone</div>
      <p class="text-sm opacity-70 mt-2 max-w-[260px] text-center">Landscape photos fit your whole vehicle in frame.</p>
      <div class="mt-8 flex items-start gap-2 max-w-[280px]">
        <Icon type="unlock" fa-style="far" class="text-sm mt-[2px] flex-shrink-0" />
        <span class="text-xs opacity-70 text-left">
          Screen not rotating? Turn off rotation lock — on iPhone swipe down from the top-right corner, on Android swipe down
          from the top, then tap the rotation lock icon.
        </span>
      </div>
    </div>

    <!-- Viewfinder / preview area (fullscreen, everything else overlays it) -->
    <video
      v-show="phase === 'camera' && cameraSupported"
      ref="videoElement"
      class="absolute inset-0 w-full h-full object-cover"
      autoplay
      playsinline
      muted
    />

    <div v-if="phase === 'starting'" class="absolute inset-0 flex flex-col items-center justify-center gap-3">
      <Icon type="circle-notch" class="text-3xl" spinner />
      <div class="text-sm opacity-70">Starting camera...</div>
    </div>

    <!-- Fallback (no in-browser camera) -->
    <div
      v-if="phase === 'camera' && !cameraSupported"
      class="absolute inset-0 flex flex-col items-center justify-center gap-4 p-8 text-center"
    >
      <div class="fallback-icon">
        <Icon type="camera" fa-style="far" class="text-3xl" />
      </div>
      <div class="text-lg font-medium">{{ label }}</div>
      <p class="text-sm opacity-70 max-w-[280px]">We'll open your device's camera so you can take this photo.</p>
    </div>

    <!-- Captured still preview -->
    <img
      v-if="phase === 'preview' && capturedUrl"
      :src="capturedUrl"
      alt="Captured photo"
      class="absolute inset-0 w-full h-full object-cover"
    />

    <!-- Top bar overlay (above the controls rail so the counter chip isn't shaded by its gradient) -->
    <div class="camera-top absolute top-0 left-0 right-0 z-30">
      <div v-if="progressPercent !== null" class="progress-track">
        <div class="progress-fill" :style="{ width: progressPercent + '%' }" />
      </div>

      <div class="flex items-center gap-3 px-4 py-3">
        <button class="close-button flex-shrink-0" aria-label="Close" @click="$emit('close')">
          <Icon type="xmark" fa-style="far" class="text-lg" />
        </button>
        <div class="flex-grow text-center min-w-0">
          <div class="text-2xl font-semibold leading-tight truncate">{{ label }}</div>
          <div v-if="hint" class="text-sm opacity-80 truncate">{{ hint }}</div>
        </div>
        <div v-if="progressLabel" class="progress-count flex-shrink-0">{{ progressLabel }}</div>
      </div>
    </div>

    <!-- Controls: bottom bar in portrait, right-hand rail in landscape -->
    <div class="camera-controls absolute z-20 px-6 pt-6 pb-8">
      <div v-if="phase === 'camera'" class="camera-actions flex items-center justify-center">
        <button v-if="cameraSupported" class="shutter-button" aria-label="Capture photo" @click="capturePhoto">
          <span class="shutter-inner" />
        </button>
        <Button v-else label="Open Camera" icon="camera" size="lg" class="accent-button" @click="openNativeCamera" />
      </div>

      <div v-else-if="phase === 'preview'" class="camera-actions">
        <div class="text-center font-medium mb-3 preview-question">Happy with this photo?</div>
        <div class="preview-buttons flex gap-3">
          <Button label="Retake" icon="rotate-left" size="lg" rounded outlined severity="secondary" block class="retake-button" @click="retake" />
          <Button label="Use photo" icon="check" size="lg" rounded block class="accent-button" @click="confirmPhoto" />
        </div>
      </div>
    </div>

    <!-- Hidden native camera input (fallback) -->
    <input ref="fallbackInput" type="file" accept="image/*" capture="environment" class="hidden" @change="onFallbackFile" />
    <canvas ref="captureCanvas" class="hidden" />
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

import Button from '@/components/button/Button.vue'
import Icon from '@/components/icon/Icon.vue'

defineProps({
  label: {
    type: String,
    required: true
  },
  hint: {
    type: String,
    default: null
  },
  progressLabel: {
    type: String,
    default: ''
  },
  progressPercent: {
    type: Number,
    default: null
  }
})

const emit = defineEmits(['close', 'captured'])

const videoElement = ref(null)
const captureCanvas = ref(null)
const fallbackInput = ref(null)

// starting | camera | preview
const phase = ref('starting')
const cameraSupported = ref(true)
const capturedBlob = ref(null)
const capturedUrl = ref(null)

let mediaStream = null

// ---------- Orientation (landscape enforced for in-browser camera) ----------

const portraitQuery = window.matchMedia('(orientation: portrait)')
const isPortrait = ref(portraitQuery.matches)

function onOrientationChange(event) {
  isPortrait.value = event.matches
}

// Only enforce landscape when we're showing our own viewfinder; the native
// camera app (fallback mode) handles its own orientation
const showRotateOverlay = computed(() => isPortrait.value && cameraSupported.value)

async function startCamera() {
  if (!navigator.mediaDevices?.getUserMedia) {
    cameraSupported.value = false
    phase.value = 'camera'
    return
  }

  try {
    mediaStream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
      audio: false
    })
    if (videoElement.value) {
      videoElement.value.srcObject = mediaStream
    }
    cameraSupported.value = true
  } catch (error) {
    console.warn(`In-browser camera unavailable, falling back to native camera: ${error.message}`)
    cameraSupported.value = false
  }
  phase.value = 'camera'
}

function stopCamera() {
  if (mediaStream) {
    mediaStream.getTracks().forEach(track => track.stop())
    mediaStream = null
  }
}

function capturePhoto() {
  const video = videoElement.value
  if (!video || !video.videoWidth) return

  const canvas = captureCanvas.value
  canvas.width = video.videoWidth
  canvas.height = video.videoHeight
  canvas.getContext('2d').drawImage(video, 0, 0)

  canvas.toBlob(
    blob => {
      if (blob) {
        setCapturedImage(blob)
      }
    },
    'image/jpeg',
    0.85
  )
}

function openNativeCamera() {
  fallbackInput.value.click()
}

function onFallbackFile(event) {
  const file = event.target.files?.[0]
  event.target.value = ''
  if (file) {
    setCapturedImage(file)
  }
}

function setCapturedImage(blob) {
  revokeCapturedUrl()
  capturedBlob.value = blob
  capturedUrl.value = URL.createObjectURL(blob)
  phase.value = 'preview'
}

function revokeCapturedUrl() {
  if (capturedUrl.value) {
    URL.revokeObjectURL(capturedUrl.value)
    capturedUrl.value = null
  }
  capturedBlob.value = null
}

function retake() {
  revokeCapturedUrl()
  phase.value = 'camera'
  if (!cameraSupported.value) {
    openNativeCamera()
  }
}

function confirmPhoto() {
  if (!capturedBlob.value) return
  const blob = capturedBlob.value
  // Hand ownership of the blob to the parent; don't revoke it here
  capturedBlob.value = null
  if (capturedUrl.value) {
    URL.revokeObjectURL(capturedUrl.value)
    capturedUrl.value = null
  }
  emit('captured', blob)
}

// Called by the parent after handling 'captured', to line up the next photo
function reset() {
  revokeCapturedUrl()
  phase.value = 'camera'
  if (!cameraSupported.value) {
    // Small delay so the heading updates before the native camera opens
    setTimeout(() => openNativeCamera(), 300)
  }
}

defineExpose({ reset })

onMounted(() => {
  portraitQuery.addEventListener('change', onOrientationChange)
  startCamera()
})

onBeforeUnmount(() => {
  portraitQuery.removeEventListener('change', onOrientationChange)
  stopCamera()
  revokeCapturedUrl()
})
</script>

<style scoped>
.camera-capture {
  background-color: #0b1526;
}

.camera-top {
  background: linear-gradient(to bottom, rgba(11, 21, 38, 0.85), rgba(11, 21, 38, 0.55) 60%, transparent);
  padding-top: env(safe-area-inset-top);
  padding-bottom: 20px;
}

.camera-controls {
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to top, rgba(11, 21, 38, 0.85), rgba(11, 21, 38, 0.55) 60%, transparent);
  padding-bottom: calc(2rem + env(safe-area-inset-bottom));
}

/* Rotate-to-landscape overlay */
.rotate-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #0b1526;
  padding: 32px;
}
.rotate-icon {
  animation: rotate-phone 2.4s ease-in-out infinite;
}
@keyframes rotate-phone {
  0%,
  25% {
    transform: rotate(0deg);
  }
  55%,
  85% {
    transform: rotate(-90deg);
  }
  100% {
    transform: rotate(-90deg);
  }
}

/* Landscape: prompt stays on top, controls move to a right-hand rail */
@media (orientation: landscape) {
  .camera-controls {
    left: auto;
    top: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    padding: 16px calc(20px + env(safe-area-inset-right)) 16px 20px;
    background: linear-gradient(to left, rgba(11, 21, 38, 0.85), rgba(11, 21, 38, 0.5) 70%, transparent);
  }

  .camera-actions {
    flex-direction: column;
    justify-content: center;
    gap: 12px;
  }

  .preview-buttons {
    flex-direction: column;
    gap: 12px;
  }

  .camera-actions .btn {
    width: 170px;
  }
}

.close-button {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.12);
}

.progress-count {
  font-size: 0.9rem;
  font-weight: 600;
  background-color: rgba(11, 21, 38, 0.65);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 100px;
  padding: 4px 12px;
}

.preview-question {
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.6);
}

/* Thin strip across the very top of the screen */
.progress-track {
  height: 3px;
  width: 100%;
  background-color: rgba(255, 255, 255, 0.15);
  overflow: hidden;
}
.progress-fill {
  height: 100%;
  background-color: var(--manufacturer-color);
  transition: width 0.4s ease;
}

.shutter-button {
  width: 74px;
  height: 74px;
  border-radius: 50%;
  border: 4px solid white;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
}
.shutter-inner {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: white;
  transition: transform 0.1s ease;
}
.shutter-button:active .shutter-inner {
  transform: scale(0.85);
  background-color: var(--manufacturer-color);
}

.fallback-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.1);
}

.accent-button {
  background-color: var(--manufacturer-color) !important;
}

.retake-button {
  color: white !important;
  border-color: rgba(255, 255, 255, 0.4) !important;
}
.retake-button:enabled:hover {
  background-color: rgba(255, 255, 255, 0.1) !important;
}
</style>
